import sanitizeHtml from 'sanitize-html';

/**
 * Broad, XSS-safe allowlist for CMS/product rich text, shared by every place that renders
 * author-supplied HTML (the generic-section renderings + the Markdown components).
 *
 * `sanitize-html` is pure JS (htmlparser2 — no jsdom/DOM), so it sanitizes IDENTICALLY on the
 * server and the client: the sanitized HTML is emitted in the SSR output (crawlable) and matches
 * on hydration, with NO jsdom dependency.
 *
 * This replaces `dompurify`, whose `sanitize` is NOT a function on the server (it needs a DOM):
 * calling it during render threw `TypeError: sanitize is not a function` and de-opted the whole
 * page to client-side rendering, leaving the content out of the server-rendered HTML. (Switching
 * to `isomorphic-dompurify` instead would pull in `jsdom`, whose transitive ESM dep throws
 * `ERR_REQUIRE_ESM` in Netlify's serverless runtime and aborts SSR — which is why the build pins
 * Node 22 and we use the pure-JS sanitizer here.)
 */
export const HTML_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
    'img',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'span',
    'figure',
    'figcaption',
    'picture',
    'source',
    'video',
    'u',
    's',
    'mark',
    'small',
    'section',
    'article',
    'header',
    'footer',
    'aside',
    'nav',
    'accordian',
    'accordion',
    'heading',
    'div',
    'details',
    'summary',
  ]),
  allowedAttributes: {
    '*': ['class', 'style', 'id', 'title', 'dir', 'lang', 'data-*', 'aria-*', 'role'],
    a: ['href', 'target', 'rel', 'name'],
    img: ['src', 'alt', 'width', 'height', 'loading', 'srcset', 'sizes'],
    video: ['src', 'controls', 'width', 'height', 'poster'],
    source: ['src', 'srcset', 'type', 'media'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan', 'scope'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowProtocolRelative: true, // keep protocol-relative + in-page (#collection) anchors
};

/** Shared with the Markdown components (kept as a named export for parity with the specify helper). */
export const MARKDOWN_SANITIZE_OPTIONS = HTML_SANITIZE_OPTIONS;

/**
 * Drop-in replacement for `dompurify`'s `sanitize` used across the CMS renderings + markdown.
 * Works on the server AND client (pure JS). The optional second argument (the old DOMPurify config
 * such as `{ ADD_ATTR: ['target'] }`) is ignored — `target` and friends are already covered by the
 * allowlist above.
 */
export const sanitize = (dirty?: string | null, _legacyDomPurifyOpts?: unknown): string =>
  typeof dirty === 'string' ? sanitizeHtml(dirty, HTML_SANITIZE_OPTIONS) : '';

/** Tags that ARE content on their own, even though stripping tags leaves no text behind. */
const HTML_MEDIA_TAG = /<(img|video|iframe|picture|source|table|hr|svg)\b/i;

/**
 * "Did the author actually write anything?" — the test to use before rendering an optional rich-text
 * CMS field. A cleared RTE comes back as `<p></p>` or `<p>&nbsp;</p>`, not as an empty string, so a
 * plain truthiness check renders an empty paragraph and its margins into the layout.
 */
export const hasHtmlContent = (html?: string | null): boolean => {
  if (typeof html !== 'string' || html === '') return false;
  if (HTML_MEDIA_TAG.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() !== '';
};
